import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import {
  ceilServiceAmount,
  canEditServiceAmount,
  hasServiceOrder,
  isServiceRowSelectable,
  isTicketServiceRow,
  resolveItineraryServicePk,
  resolveTicketServiceEstado,
  showServiceDate,
  syncServiceTotalFromTickets
} from 'src/entities/file/lib/service-table.utils';
import { isOrderEditable as isOrderEditableFn } from 'src/entities/order/model/order-status.model';
import { isPurchaseNoteEditable as isPurchaseNoteEditableFn } from 'src/entities/purchase-note';
import {
  MASS_OS_SERVICE_COLUMNS,
  SERVICE_TABLE_COLUMN_HEADERS
} from 'src/widgets/detail-file-service-table/service-table.config';

@Component({
  selector: 'app-mass-os-services-table',
  templateUrl: './mass-os-services-table.component.html',
  styleUrls: ['./mass-os-services-table.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ]
})
export class MassOsServicesTableComponent {
  @Input() dataSource!: MatTableDataSource<any>;
  @Input() model: any[] = [];
  @Input() isSSFF = false;
  @Input() selectedCount = 0;
  @Input() selectedForOsCount = 0;
  @Input() selectedForNpCount = 0;
  @Input() isCreateOrderBusy = false;
  @Input() isCreatePurchaseNoteBusy = false;
  @Input() isLoading = false;

  @Output() generateOrders = new EventEmitter<void>();
  @Output() generatePurchaseNotes = new EventEmitter<void>();
  @Output() selectionChanged = new EventEmitter<void>();
  @Output() modelChanged = new EventEmitter<void>();
  @Output() editPaymentDate = new EventEmitter<any>();
  @Output() editReviewDate = new EventEmitter<any>();
  @Output() editTarifa = new EventEmitter<any>();

  readonly displayedColumns = [...MASS_OS_SERVICE_COLUMNS, 'expand'];
  readonly headers = SERVICE_TABLE_COLUMN_HEADERS;

  readonly isOrderEditable = isOrderEditableFn;
  readonly isPurchaseNoteEditable = isPurchaseNoteEditableFn;
  readonly hasServiceOrder = hasServiceOrder;
  readonly canEditAmount = (row: any) => canEditServiceAmount(row, this.isSSFF);
  readonly ceil = ceilServiceAmount;
  readonly ticketServiceEstado = resolveTicketServiceEstado;

  readonly estadoServicioTicketsLabel: Record<string, string> = {
    CREADO: 'Creado',
    EN_PROCESO: 'En Proceso',
    RESERVADO: 'Reservado',
    NUEVA_ADICION: 'Nueva Adición'
  };

  /** Varios servicios ticket pueden quedar expandidos a la vez. */
  expandedRowIds = new Set<string>();

  constructor(
    private readonly fileService: FileService,
    private readonly changeDetectorRefs: ChangeDetectorRef
  ) {}

  get isBusy(): boolean {
    return this.isCreateOrderBusy || this.isCreatePurchaseNoteBusy;
  }

  @ViewChild(MatPaginator) set paginator(value: MatPaginator) {
    if (this.dataSource) {
      this.dataSource.paginator = value;
    }
  }

  @ViewChild(MatSort) set sort(value: MatSort) {
    if (this.dataSource) {
      this.dataSource.sort = value;
    }
  }

  isTicketRow(row: any): boolean {
    return String(row?.tipoServicio ?? '').toUpperCase() === 'TICKET';
  }

  private rowExpandKey(row: any): string {
    return String(row?.id ?? '');
  }

  isRowExpanded(row: any): boolean {
    const key = this.rowExpandKey(row);
    return Boolean(key) && this.expandedRowIds.has(key);
  }

  async toggleExpandRow(row: any, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.isTicketRow(row)) {
      return;
    }

    const key = this.rowExpandKey(row);
    if (!key) return;

    if (this.expandedRowIds.has(key)) {
      this.expandedRowIds.delete(key);
      this.expandedRowIds = new Set(this.expandedRowIds);
      return;
    }

    await this.ensureRowExpanded(row);
  }

  async ensureRowExpanded(row: any): Promise<void> {
    if (!this.isTicketRow(row)) return;
    const key = this.rowExpandKey(row);
    if (!key) return;

    if (!this.expandedRowIds.has(key)) {
      this.expandedRowIds = new Set(this.expandedRowIds).add(key);
    }
    await this.loadTicketExpandDetails(row);
    this.changeDetectorRefs.detectChanges();
  }

  /** Recarga tickets de filas expandidas sin cerrar el expand (post Generar OS). */
  async refreshExpandedTicketRows(): Promise<void> {
    if (!this.expandedRowIds.size) return;
    const expanded = (this.model ?? []).filter((row) =>
      this.expandedRowIds.has(this.rowExpandKey(row))
    );
    await Promise.all(expanded.map((row) => this.loadTicketExpandDetails(row)));
    this.changeDetectorRefs.detectChanges();
  }

  /** Check de ticket en expand → marca padre y habilita Generar OS. */
  onTicketSelectionChange(event: {
    servicioId: string;
    ticketId: string;
    selected: boolean;
  }): void {
    const { servicioId, ticketId, selected } = event;
    const service = (this.model ?? []).find(
      (row) =>
        String(row?.id) === String(servicioId) ||
        String(row?.serviceId) === String(servicioId)
    );
    if (!service || !Array.isArray(service.tickets)) return;

    const ticket = service.tickets.find(
      (t: any) => String(t?.id) === String(ticketId)
    );
    if (ticket) {
      ticket.checked = selected;
    }

    const hasCheckedTickets = service.tickets.some(
      (t: any) => t?.checked === true && !t?.order?.orderId
    );
    service.checked = hasCheckedTickets
      ? true
      : Boolean(service.checked) && this.isRowSelectable(service);

    this.selectionChanged.emit();
    this.changeDetectorRefs.detectChanges();
  }

  /**
   * Tras borrar/crear OS en expand: refresca tickets y deja seleccionable
   * el servicio si hay tickets CREADO sin OS.
   */
  async onTicketsOrderChanged(event: {
    serviceId: string;
    serviceIds?: string[];
  }): Promise<void> {
    const ids = [
      ...new Set(
        (event?.serviceIds?.length
          ? event.serviceIds
          : [event?.serviceId]
        ).filter(Boolean)
      )
    ];
    if (!ids.length) return;

    for (const serviceId of ids) {
      const service = (this.model ?? []).find(
        (row) =>
          String(row?.id) === String(serviceId) ||
          String(row?.serviceId) === String(serviceId)
      );
      if (!service) continue;

      this.syncTicketParentAvailability(service);
      await this.loadTicketExpandDetails(service);
      this.syncTicketParentAvailability(service);
    }

    this.selectionChanged.emit();
    this.changeDetectorRefs.detectChanges();
  }

  private syncTicketParentAvailability(service: any): void {
    const tickets = Array.isArray(service?.tickets) ? service.tickets : [];
    const hasOrdered = tickets.some((ticket: any) =>
      Boolean(ticket?.order?.orderId)
    );
    const hasCreadoLibre = tickets.some((ticket: any) => {
      const estado = String(ticket?.estadoTicket ?? '')
        .toUpperCase()
        .replace(/\s+/g, '_');
      const normalized = estado === 'CREATED' ? 'CREADO' : estado;
      return !ticket?.order?.orderId && normalized === 'CREADO';
    });

    service.hasOrderedTickets = hasOrdered;
    if (!hasOrdered || hasCreadoLibre) {
      service.order = null;
    }

    if (hasCreadoLibre && hasOrdered) {
      service.estadoServicioTickets = 'EN_PROCESO';
    } else if (hasCreadoLibre) {
      service.estadoServicioTickets = 'CREADO';
    } else if (hasOrdered) {
      service.estadoServicioTickets = 'EN_PROCESO';
    } else {
      service.estadoServicioTickets = 'CREADO';
    }
  }

  isRowSelectable(row: any): boolean {
    return isServiceRowSelectable(row, this.isSSFF);
  }

  isAllSelected(): boolean {
    const selectable =
      this.dataSource?.data?.filter((row) => this.isRowSelectable(row)) ?? [];
    if (!selectable.length) {
      return false;
    }
    return selectable.every((row) => row.checked);
  }

  toggleAllRows(): void {
    const next = !this.isAllSelected();
    (this.dataSource?.data ?? []).forEach((row) => {
      if (this.isRowSelectable(row)) {
        row.checked = next;
        if (next && this.isTicketRow(row)) {
          void this.ensureRowExpanded(row);
        }
      }
    });
    this.selectionChanged.emit();
  }

  updateCheck(row: any): void {
    if (!this.isRowSelectable(row)) {
      return;
    }
    row.checked = !row.checked;
    this.selectionChanged.emit();
    if (row.checked && this.isTicketRow(row)) {
      void this.ensureRowExpanded(row);
    }
  }

  serviceDate(row: any): Date | null {
    if (row.serviceDate instanceof Date) {
      return row.serviceDate;
    }
    if (!row.dateRange || row.day == null) {
      return null;
    }
    return showServiceDate(row.dateRange, String(row.day));
  }

  serviceAmount(row: any): string {
    return this.ceil(row.total);
  }

  private async loadTicketExpandDetails(service: any): Promise<void> {
    const serviceId = resolveItineraryServicePk(service);
    const idItinerary = service?.idItinerary;
    if (!serviceId || !idItinerary) {
      service.tickets = [];
      service.ticketsLoaded = true;
      return;
    }

    // Siempre recargar al expandir (mismo patrón que Ticketería)
    service.ticketsLoading = true;
    this.changeDetectorRefs.detectChanges();

    try {
      const res = await firstValueFrom(
        this.fileService.getTicketeriaTicketsByService({
          serviceId,
          idItinerary
        })
      );
      const tickets = res?.getTicketeriaTicketsByService?.tickets ?? [];
      const prevChecked = new Set(
        (service.tickets || [])
          .filter((t: any) => t?.checked === true)
          .map((t: any) => String(t.id))
      );
      service.tickets = tickets.map((ticket: any) => ({
        ...ticket,
        checked: prevChecked.has(String(ticket.id))
      }));
      service.ticketsLoaded = true;
      syncServiceTotalFromTickets(service);
      service.hasOrderedTickets = service.tickets.some(
        (ticket: any) => Boolean(ticket?.order?.orderId)
      );
      service.hasInvalidFechaOperacion = service.tickets.some(
        (ticket: any) => ticket.hasInvalidFechaOperacion === true
      );
      const hasCheckedTickets = service.tickets.some(
        (t: any) => t?.checked === true && !t?.order?.orderId
      );
      if (hasCheckedTickets) {
        service.checked = true;
      } else if (service.checked && !this.isRowSelectable(service)) {
        service.checked = false;
        this.selectionChanged.emit();
      }
      this.syncTicketParentAvailability(service);
    } catch (error) {
      console.error('Error al cargar tickets del servicio (OS masivas):', error);
      service.tickets = [];
      service.ticketsLoaded = true;
    } finally {
      service.ticketsLoading = false;
      this.changeDetectorRefs.detectChanges();
    }
  }
}
